import { FullscreenOverlay } from "./FullscreenOverlay";

const Code = ({ children }) => (
  <code className="inline-block rounded-md bg-[#2d2824] px-1.5 py-1 font-mono text-[12px] font-bold leading-tight text-[#fdfbf7]">
    {children}
  </code>
);

const Row = ({ code, children }) => (
  <div className="border-b border-[#efe9df] py-2.5 last:border-0 sm:flex sm:items-start sm:gap-3">
    <div className="flex flex-wrap gap-1 sm:w-[36%] sm:shrink-0">
      {code.map((c, i) => (
        <Code key={i}>{c}</Code>
      ))}
    </div>
    <p className="mt-1 text-[13px] font-semibold leading-snug text-[#4a423a] sm:mt-0 sm:flex-1">
      {children}
    </p>
  </div>
);

const Section = ({ title, children }) => (
  <section className="rounded-2xl border border-[#e5e0d8] bg-white/70 p-4">
    <h4 className="mb-1 text-[11px] font-extrabold uppercase tracking-widest text-[#8a2be2]">
      {title}
    </h4>
    {children}
  </section>
);

// the cheat-sheet itself, also shown in Social Studio posts
export const AbcGuideBody = () => (
  <>
        <Section title="Pitch & octaves">
          <Row code={["C", "D", "E", "F", "G", "A", "B"]}>
            Middle octave, from middle C upwards.
          </Row>
          <Row code={["c", "d", "e"]}>Lower case = one octave higher.</Row>
          <Row code={["c'", "c''"]}>Each apostrophe lifts another octave.</Row>
          <Row code={["C,", "C,,"]}>Each comma drops an octave.</Row>
          <Row code={["z", "z2", "Z"]}>Rest, longer rest, whole-bar rest.</Row>
        </Section>

        <Section title="Note length">
          <Row code={["L:1/8"]}>
            The header sets the default length — in session tunes one letter is normally an eighth
            note (quaver).
          </Row>
          <Row code={["A2", "A3", "A4"]}>Two, three or four times the default length.</Row>
          <Row code={["A/", "A//"]}>Half, then a quarter of the default length.</Row>
          <Row code={["A>B", "A<B"]}>
            Dotted swing: the first note borrows from the second (or the other way round).
          </Row>
        </Section>

        <Section title="Accidentals">
          <Row code={["^F", "^^F"]}>Sharp, double sharp.</Row>
          <Row code={["_B", "__B"]}>Flat, double flat.</Row>
          <Row code={["=F"]}>
            Natural — cancels the key signature or an earlier accidental in the same bar.
          </Row>
          <Row code={["K:D", "K:Ador", "K:Emin"]}>
            Key and mode. Anything not written explicitly follows this key signature.
          </Row>
        </Section>

        <Section title="Bars, repeats & endings">
          <Row code={["|"]}>Bar line.</Row>
          <Row code={["|:", ":|"]}>Start and end of a repeat.</Row>
          <Row code={["::", ":|:"]}>End one repeat and start the next.</Row>
          <Row code={["|1", ":|2"]}>First and second time endings (variant endings).</Row>
          <Row code={["||", "|]"]}>Section break, final bar line.</Row>
        </Section>

        <Section title="Grouping & phrasing">
          <Row code={["(A B)"]}>Slur — play the notes joined.</Row>
          <Row code={["A-A"]}>Tie — hold one note across the bar or beat.</Row>
          <Row code={["(3ABC"]}>Triplet. Use (5 or (7 for other tuplets.</Row>
          <Row code={["[CEG]"]}>Chord: notes sounding together.</Row>
          <Row code={["{ag}f"]}>Grace notes before the main note.</Row>
        </Section>

        <Section title="Ornaments & articulation">
          <Row code={["~G"]}>Irish roll (the general ornament sign).</Row>
          <Row code={["!trill!A", "!turn!A"]}>
            Named ornaments, written between exclamation marks.
          </Row>
          <Row code={["u", "v"]}>Up-bow and down-bow for fiddlers.</Row>
          <Row code={[".A"]}>Staccato — shorten the note.</Row>
          <Row code={["!fermata!A", "!accent!A"]}>Fermata, accent and the rest of the family.</Row>
        </Section>

        <Section title="Headers & text">
          <Row code={["X:", "T:", "R:"]}>Number, title, rhythm (reel, jig, …).</Row>
          <Row code={["M:6/8", "M:4/4"]}>Meter.</Row>
          <Row code={['"Am"', '"D/F#"']}>
            Guitar chords above the staff — they transpose with the tune.
          </Row>
          <Row code={['"^Slowly"']}>
            A text annotation: the first character places it (^ above, _ below).
          </Row>
          <Row code={["%"]}>A comment: everything after it is ignored.</Row>
        </Section>
  </>
);

export const AbcHelpDialog = ({ open, onOpenChange }) => (
  <FullscreenOverlay
    open={open}
    onClose={() => onOpenChange(false)}
    title="Reading & writing ABC"
    zClass="z-[95]"
  >
    <div className="mx-auto max-w-2xl space-y-3 pb-8" data-testid="abc-help-dialog">
      <p className="text-sm font-semibold text-[#6e655b]">
        ABC is plain text that sounds like music. Edit it in the tune viewer and the notation
        and the player follow instantly.
      </p>

      <AbcGuideBody />

      <p className="px-1 text-xs font-semibold text-[#6e655b]">
        Tip: keep one line of ABC per line of music you want on paper — the PDF export follows
        your own line breaks.
      </p>

      <button
        onClick={() => onOpenChange(false)}
        className="w-full rounded-full bg-[#2d2824] py-3.5 text-sm font-extrabold text-white transition-transform active:scale-95"
        data-testid="abc-help-close-bottom"
      >
        Close
      </button>
    </div>
  </FullscreenOverlay>
);
